import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import {
  HotelDetails,
  SearchParameters,
  wsAvailability,
} from "../../../assets/Services/wsSearch";
import { useTranslation } from "react-i18next";
import { currencySymbols } from "../../../assets/Utils/prices";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { ANALYTICS_SORT_BY } from "../../../config/analytics";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/userSlice";
import IconTelephone from "../../../assets/icons/IconTelephone";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import CollectionController from "../../../assets/Controllers/CollectionController";
import { useSearch } from "../../hooks/useSearch";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { getDisplayPrice, getOTADeltaPrice } from "../Directsearch/utils";
import Bowser from 'bowser';
import { toast } from "react-toastify";
import { useCopyToClipboard } from "../../hooks/hooks";
import { closeModal, openModal } from "../../../assets/Utils/modal";
import imageBadgePhoneNumber from "../../../assets/img/directsearch/badge_phone_number.avif";
import IconCopy from "../../../assets/icons/IconCopy";

type Props = {
  hotelDetail: HotelDetails;
  availability?: wsAvailability;
  searchParameters?: SearchParameters;
  isSmallScreen: boolean;
  isExtraDiscountApplied: boolean;
};

const AvailabilityCardMobile = (props: Props) => {
  const {
    hotelDetail,
    availability,
    searchParameters,
    isSmallScreen,
    isExtraDiscountApplied,
  } = props;
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const { buildRedirectURL } = useSearch();

  const urlGoogle = `https://google.com/search?q=${encodeURIComponent(
    `${hotelDetail?.name.replace(/&/g, "")} ${
      searchParameters?.destinationLabel?.split(",")[0]
    }`,
  )}`;

  let urlWebsite = hotelDetail?.website;
  if (urlWebsite && !urlWebsite.includes("http"))
    urlWebsite = `https://${urlWebsite}`;

  const fallbackUrl = urlWebsite ?? urlGoogle;

  const bowserParser = Bowser.getParser(navigator.userAgent);
  const isMobile = bowserParser.getPlatformType() === "mobile"

  const { copyToClipboard } = useCopyToClipboard();


  if (!searchParameters) return null;

  if (!availability) return null;

  return (
    <div className="flex flex-col items-center overscroll-contain shadow-xl">
      {isExtraDiscountApplied && hotelDetail.isActive && (
        <div className="w-full bg-black rounded-t-2xl flex items-center justify-center px-4 h-6">
          <p className="text-white text-xs font-medium">
            {t("ds.loyalty_discount_with_code", {
              percentage: hotelDetail?.loyatyDiscountPercentage,
            })}
          </p>
        </div>
      )}
      <div className="w-full flex justify-between items-center gap-2 px-2 py-3 bg-white border border-[#E8E8E8]">
        <div className="flex flex-col items-center justify-center gap-1 w-1/2">
          {availability.availabilityInfo.available && (
            <div className="flex justify-between items-end gap-0.5">
              <p className="text-xs font-medium">{t("ds.from2")}</p>
              <p className="font-medium text-2xl translate-y-1">
                {getDisplayPrice({
                  availability,
                  deltaBooking: hotelDetail.deltaPriceBookingCom,
                  deltaHotels: hotelDetail.deltaPriceHotelsCom,
                  lang: searchParameters.language,
                })}
              </p>
            </div>
          )}
          {!availability.availabilityInfo.available && (
            <div className="flex rounded-lg px-2 py-1 justify-center items-center bg-[#E8E8E8]">
              <p className="text-xs font-medium text-[#747474]">
                {t(
                  isSmallScreen
                    ? "ds.no_availability_short"
                    : "ds.no_availability",
                )}
              </p>
            </div>
          )}
          {availability.availabilityInfo.available ? (
            <div className="flex items-center justify-center">
              <p className="text-xs text-[#EF4444] font-medium text-center">
                {getOTADeltaPrice({
                  availability,
                  deltaBooking: hotelDetail.deltaPriceBookingCom,
                  deltaHotels: hotelDetail.deltaPriceHotelsCom,
                  lang: searchParameters.language,
                })}
              </p>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 w-fit">
          <a
            href={`tel:${hotelDetail.phone}`}
            className="flex items-center justify-center size-12 aspect-square bg-[#F3F3F3] rounded-full no-underline"
            onClick={(event) => {
              fireTagManagerEvent("call_hotel", {
                destination: !!searchParameters?.destinationLabel
                  ? searchParameters.destinationLabel
                  : null,
                is_price_loading: !availability,
                amount: availability
                  ? Number(availability.availabilityInfo.value.toFixed(0))
                  : 0,
                collection_id: hotelDetail._id,
                collection_name: hotelDetail.name,
                is_available:
                  availability && availability.availabilityInfo.available,
                is_free: !hotelDetail.isActive,
                is_loyalty_enabled:
                  !!hotelDetail.isLoyaltyEnabled &&
                  !!hotelDetail?.isLoyaltyEnabledAdmin,
                list_position: null,
                source: "Details",
                user_id: user?._id ?? null,
                stars:
                  hotelDetail.stars && Number(hotelDetail.stars) !== 0
                    ? Number(hotelDetail.stars)
                    : null,
                tag: !!searchParameters?.tag ? searchParameters.tag : null,
                adults: searchParameters?.adults ?? null,
                children: searchParameters?.childrenAge.length,
                sort_type: searchParameters?.sortBy
                  ? ANALYTICS_SORT_BY[
                      (
                        {
                          recommended: "RECOMMENDED",
                          distance_to_cc: "DISTANCE",
                          reviews_score: "REVIEWS",
                          price: "PRICE",
                        } as const
                      )[searchParameters.sortBy]
                    ]
                  : ANALYTICS_SORT_BY.RECOMMENDED,
                categories_filter: JSON.stringify(searchParameters?.filters ?? {}),
                check_in: searchParameters?.arrivalDate
                  ? format(
                      new Date(searchParameters.arrivalDate),
                      "dd-MM-yyyy",
                      {
                        locale: it,
                      },
                    )
                  : null,
                check_out: searchParameters?.departureDate
                  ? format(
                      new Date(searchParameters.departureDate),
                      "dd-MM-yyyy",
                      {
                        locale: it,
                      },
                    )
                  : null,
                includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
              });

              if (!isMobile) {
                            event.preventDefault();
                            openModal({
                              content: (
                                <div className="flex flex-col p-8 items-center justify-center gap-4 flex-1">
                                  <button
                                      type="button"
                                      onClick={closeModal}
                                      className="bg-transparent border-none touch-hitbox absolute top-4 right-4"
                                  >
                                    <IconNew icon="close" className="size-5" />
                                  </button>
                                  <img src={imageBadgePhoneNumber} role="presentation" className="size-24 shrink-0 mx-auto" />
                                  <div className="flex flex-col gap-1">
                                    <h3 className="font-medium text-[#747474] text-center mb-0 text-base">
                                      {t("ds.call_structure", {
                                        name: hotelDetail.name
                                      })}
                                    </h3>
                                    <p className="text-lg font-medium text-black text-center">{hotelDetail.phone}</p>
                                  </div>
                                  <div className="flex items-center">
                                    <button type="button" className="flex items-center gap-1 bg-transparent border-none text-[#A2A2A2] flex-1" onClick={() => {
                                      copyToClipboard(hotelDetail.phone ?? "");
                                      toast.success(t("ds.phone_number_copied"));
                                    }}>
                                      <IconCopy className="size-4" />
                                      <p className="text-xs font-medium">{t("ds.copy")}</p>
                                    </button>
                                  </div>
                                  {isExtraDiscountApplied && (
                                    <p className="text-sm font-medium text-center mt-8 text-[#747474] flex flex-wrap items-center gap-1 justify-center">
                                      <span>
                                        {t("ds.dont_forget_discount.0")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.1", {
                                          discountCode: "TAKYON"
                                        })}
                                      </span>
                                      <span>
                                        {t("ds.dont_forget_discount.2")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.3", {
                                          percentage: hotelDetail.loyatyDiscountPercentage
                                        })}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              ),
                              className: "rounded-t-3xl h-fit self-end lg:h-auto lg:self-center lg:rounded-3xl lg:max-w-lg"
                            }, {
                              hideModalCloseBtn: true,
                            })
                          }
            }}
          >
            <IconTelephone className="size-5 text-black" />
          </a>

          {hotelDetail.isActive ? (
            <Button
              size="sm"
              className="h-12"
              textClass="text-sm font-medium"
              text={t("ds.book_webiste")}
              onClick={() => {
                CollectionController.clickOnCollection(hotelDetail._id, {
                  btn: "visit_website",
                  directPrice: availability?.availabilityInfo.value,
                  cutPrice: undefined,
                  bookingPrice: undefined,
                  hotelsPrice: "N/A",
                  bookingComId: hotelDetail.bookingId,
                  checkin: searchParameters?.arrivalDate,
                  checkout: searchParameters?.departureDate,
                });
                const newTabUrl = buildRedirectURL(
                  hotelDetail._id,
                  fallbackUrl,
                );
                fireTagManagerEvent("book_on_site", {
                  destination: !!searchParameters?.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  is_price_loading: !availability,
                  amount: availability
                    ? Number(availability.availabilityInfo.value.toFixed(0))
                    : 0,
                  collection_id: hotelDetail._id,
                  collection_name: hotelDetail.name,
                  is_available:
                    availability && availability.availabilityInfo.available,
                  is_free: !hotelDetail.isActive,
                  is_loyalty_enabled:
                    !!hotelDetail.isLoyaltyEnabled &&
                    !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null,
                  user_id: user?._id ?? null,
                  stars:
                    hotelDetail.stars && Number(hotelDetail.stars) !== 0
                      ? Number(hotelDetail.stars)
                      : null,
                  tag: !!searchParameters?.tag ? searchParameters.tag : null,
                  adults: searchParameters?.adults ?? null,
                  children: searchParameters?.childrenAge.length,
                  sort_type: searchParameters?.sortBy
                    ? ANALYTICS_SORT_BY[
                        (
                          {
                            recommended: "RECOMMENDED",
                            distance_to_cc: "DISTANCE",
                            reviews_score: "REVIEWS",
                            price: "PRICE",
                          } as const
                        )[searchParameters.sortBy]
                      ]
                    : ANALYTICS_SORT_BY.RECOMMENDED,
                  categories_filter: JSON.stringify(searchParameters?.filters ?? {}),
                  check_in: searchParameters?.arrivalDate
                    ? format(
                        new Date(searchParameters.arrivalDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  check_out: searchParameters?.departureDate
                    ? format(
                        new Date(searchParameters.departureDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                });
                window.open(newTabUrl, "_blank");
              }}
              disabled={
                !availability || !availability.availabilityInfo.available
              }
            />
          ) : (
            <a
              type="button"
              className="bg-[#F3F3F3] text-xs rounded-xl p-4 w-full flex items-center justify-center border-none text-black"
              href={hotelDetail.website}
              target="_blank"
              rel="noreferrer"
              style={
                !hotelDetail.website
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}
              }
            >
              <IconNew icon="captive_portal" type="symbol" size={20} /> Sito Web
              {/* {t("ds.read_more")} */}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCardMobile;
